/**
 * ────────────────────────────────────────────────────────────────────────────
 * Notification Action Resolver
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Central registry that maps backend `action_type` values to frontend routes.
 *
 * HOW TO ADD A NEW MODULE:
 *   1. Define the action_type → route mapping in the `ACTION_ROUTE_MAP`.
 *   2. If the route needs dynamic segments from `params`, use a function.
 *   3. If the route is static, use a plain string.
 *
 * The resolver returns `null` for unknown action types — callers should
 * gracefully handle that (e.g. do nothing on click).
 * ────────────────────────────────────────────────────────────────────────────
 */

import { useRBACStore } from '../stores/useRBACStore';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Safely parse the `params` JSON string from a notification's data object.
 * Returns an empty object on failure.
 *
 * @param {string|undefined} paramsString
 * @returns {Record<string, unknown>}
 */
export const parseNotificationParams = (paramsString) => {
  if (!paramsString) return {};
  try {
    return JSON.parse(paramsString);
  } catch {
    return {};
  }
};

// ─── Route builders (keep each module in its own section) ───────────────────

// ── CRM ──
const toCRMSettings = () => '/crm/settings';
const toAttendanceLocations = (params) => {
  const lat = params?.lat;
  const lng = params?.lng || params?.lang;
  if (lat && lng) {
    return `/attendance/locations?location-view=list&lat=${lat}&lng=${lng}`;
  }
  return '/attendance/locations';
};
const toLeadDetails = (params) => {
  const leadId = params?.leadId || params?.leadid || params?.id;
  return leadId ? `/crm/lead/${leadId}/details` : '/crm/leads';
};
const toCRMActivities = (params) => {
  const leadId = params?.leadId || params?.leadid || params?.id;
  // If we have an activityId, we might want to deep link, but for now lead details is the primary destination
  return leadId ? `/crm/lead/${leadId}/details` : '/crm/activities';
};
const toCRMFollowups = (params) => {
  const leadId = params?.leadId || params?.leadid || params?.id;
  // If we have a followUpId, we might want to deep link
  return leadId ? `/crm/lead/${leadId}/details` : '/crm/followups';
};

// ── Projects ──
const toProjects = () => '/';
const toProjectDetails = (params) => {
  const pId = params?.projectId || params?.projectid || params?.pID;
  return pId ? `/project/${pId}/details` : '/';
};
const toProjectPage = (params, page) => {
  const pId = params?.projectId || params?.projectid || params?.pID;
  return pId ? `/project/${pId}/${page}` : '/';
};
const toProjectTask = (params, defaultTab = 'details') => {
  const pId = params?.projectId || params?.projectid || params?.pID;
  const taskId = params?.taskId || params?.taskid;
  const taskPage = params?.taskPage || 'active';
  const issueId = params?.issueId || params?.issueid;
  const checklistId = params?.checklistId || params?.checklistid;
  const computedTab = issueId ? 'issues' : (checklistId ? 'checklist' : defaultTab);
  const taskManagerPage = params?.taskManagerPage || computedTab;

  if (pId && taskId) {
    return `/project/${pId}/tasks/${taskPage}/${taskId}/${taskManagerPage}`;
  }
  return pId ? `/project/${pId}/tasks` : '/';
};
const toProjectMI = (params) => {
  const pId = params?.projectId || params?.projectid || params?.pID;
  const mId = params?.indentId || params?.indentid || params?.mId;
  return pId && mId
    ? `/project/${pId}/material-indent/${mId}/view`
    : pId
    ? `/project/${pId}/material-indent`
    : '/';
};
const toProjectPO = (params) => {
  const pId = params?.projectId || params?.projectid || params?.pID;
  const poId = params?.purchaseOrderId || params?.purchaseorderid || params?.poId;
  return pId && poId
    ? `/project/${pId}/purchase-orders/${poId}/view`
    : pId
    ? `/project/${pId}/purchase-orders`
    : '/';
};
const toProjectLogistics = (params) => {
  const pId = params?.projectId || params?.projectid || params?.pID;
  const lId = params?.logisticId || params?.logisticsId || params?.logisticsid || params?.id;
  return pId && lId
    ? `/project/${pId}/logistics/${lId}/view`
    : pId
    ? `/project/${pId}/logistics`
    : '/';
};

// ── Global Tasks ──
const toGlobalTask = (params, defaultTab = 'details') => {
  const taskId = params?.taskId || params?.taskid;
  const taskPage = params?.taskPage || 'active';
  const issueId = params?.issueId || params?.issueid;
  const checklistId = params?.checklistId || params?.checklistid;
  const computedTab = issueId ? 'issues' : (checklistId ? 'checklist' : defaultTab);
  const taskManagerPage = params?.taskManagerPage || computedTab;

  if (taskId) {
    return `/task-manager/${taskPage}/${taskId}/${taskManagerPage}`;
  }
  return '/task-manager';
};
const toGlobalSubTask = (params, defaultTab = 'details') => {
  const taskId = params?.taskId || params?.taskid;
  const subTaskId = params?.subTaskId || params?.subtaskid;
  const taskPage = params?.taskPage || 'active';
  const issueId = params?.issueId || params?.issueid;
  const checklistId = params?.checklistId || params?.checklistid;
  const computedTab = issueId ? 'issues' : (checklistId ? 'checklist' : defaultTab);
  const taskManagerPage = params?.taskManagerPage || computedTab;

  if (subTaskId) {
    return `/subtask-manager/${taskPage}/${subTaskId}/${computedTab}`;
  }
  if (taskId) {
    return `/task-manager/${taskPage}/${taskId}/${computedTab}`;
  }
  return '/task-manager';
};

// ── Global Logistics / Inventory ──
const toWarehouse = (params) => {
  const wId = params?.warehouseId || params?.warehouseid || params?.id;
  return wId ? `/warehouse/${wId}/details` : '/warehouse';
};
const toGlobalMI = (params) => {
  const mId = params?.indentId || params?.indentid || params?.mId;
  return mId ? `/materials-indent/${mId}/view` : '/materials-indent';
};
const toGlobalPO = (params) => {
  const poId = params?.purchaseOrderId || params?.purchaseorderid || params?.poId;
  return poId ? `/purchases/purchase-order/${poId}/view` : '/purchases/purchase-order';
};
const toGlobalPOBill = (params) => {
  const billId = params?.poBillId || params?.pobillid || params?.id;
  return billId ? `/purchases/po-bills/${billId}/view` : '/purchases/po-bills';
};
const toGlobalExpense = (params) => {
  const id = params?.expenseId || params?.expenseid || params?.id;
  return id ? `/purchases/expenses/edit/${id}` : '/purchases/expenses';
};
const toStockLog = (params) => {
  const stockId = params?.stockId || params?.stockid;
  return stockId ? `/stocks/stock/log?stockId=${stockId}` : '/stocks';
};

// ── Global Settings / Admin ──
const toEmployeeDetails = (params) => {
  const id = params?.userId || params?.userid || params?.id;
  return id ? `/settings/employees/${id}` : '/settings/employees';
};
const toRoleDetails = (params) => {
  const id = params?.roleId || params?.roleid || params?.id;
  return id ? `/settings/roles/${id}` : '/settings/roles';
};
const toVehicleDetails = (params) => {
  const id = params?.vehicleId || params?.vehicleid || params?.id;
  return id ? `/logistics/vehicles` : '/logistics/vehicles'; // Table says vehicles page, but shows vehicleId is available
};
const toGlobalLogisticsDetails = (params) => {
  const id = params?.logisticId || params?.logisticsid || params?.id;
  return id ? `/logistics` : '/logistics';
};

// ─── Action → Route Map ────────────────────────────────────────────────────

const ACTION_ROUTE_MAP = {
  // ─── PROJECT RELATED ──────────────────────────────────────────────────────
  PROJECT_CREATE: toProjectDetails,
  PROJECT_UPDATE: toProjectDetails,
  PROJECT_DELETE: toProjects,
  PROJECT_PAYMENT_UPDATE_INSTALLMENT: (p) => toProjectPage(p, 'payment-tracker'),
  PROJECT_PAYMENT_ADD_PART_PAYMENT: (p) => toProjectPage(p, 'payment-tracker'),
  PROJECT_PAYMENT_EDIT_PART_PAYMENT: (p) => toProjectPage(p, 'payment-tracker'),
  PROJECT_TASK_CREATE: (p) => toProjectTask(p, 'details'),
  PROJECT_TASK_UPDATE: (p) => toProjectTask(p, 'details'),
  PROJECT_TASK_ASSIGNEE: (p) => toProjectTask(p, 'details'),
  PROJECT_TASK_RAISE_ISSUE: (p) => toProjectTask(p, 'issues'),
  PROJECT_TASK_ISSUE_ASSIGNEE: (p) => toProjectTask(p, 'issues'),
  PROJECT_TASK_FILE_UPLOAD: (p) => toProjectTask(p, 'files'),
  PROJECT_TASK_CHECKLIST: (p) => toProjectTask(p, 'checklist'),
  PROJECT_TASK_LABOUR_ATTENDANCE: (p) => toProjectPage(p, 'labour-attendance'),
  PROJECT_TASK_STOCK_UPDATE: (p) => toProjectPage(p, 'stocks'),
  PROJECT_SUBTASK_CREATE: (p) => toProjectTask(p, 'details'),
  PROJECT_SUBTASK_ASSIGNEE: (p) => toProjectTask(p, 'details'),
  PROJECT_SUBTASK_RAISE_ISSUE: (p) => toProjectTask(p, 'issues'),
  PROJECT_SUBTASK_ISSUE_ASSIGNEE: (p) => toProjectTask(p, 'issues'),
  PROJECT_SUBTASK_FILE_UPLOAD: (p) => toProjectTask(p, 'files'),
  PROJECT_SUBTASK_CHECKLIST: (p) => toProjectTask(p, 'checklist'),
  PROJECT_SUBTASK_LABOUR_ATTENDANCE: (p) => toProjectPage(p, 'labour-attendance'),
  PROJECT_SUBTASK_STOCK_UPDATE: (p) => toProjectPage(p, 'stocks'),
  PROJECT_FILE_UPLOAD: (p) => toProjectPage(p, 'files'),
  PROJECT_FILE_DELETE: (p) => toProjectPage(p, 'files'),
  PROJECT_MI_CREATE: toProjectMI,
  PROJECT_MI_EDIT: toProjectMI,
  PROJECT_MI_DELETE: toProjectMI,
  PROJECT_MI_APPROVAL: toProjectMI,
  PROJECT_MI_HOLD: toProjectMI,
  PROJECT_MI_ASSIGN: toProjectMI,
  PROJECT_MI_DISPATCH_LOGISTICS: toProjectMI,
  PROJECT_MI_DISPATCH_PO: toProjectMI,
  PROJECT_PO_CREATE: toProjectPO,
  PROJECT_PO_EDIT: toProjectPO,
  PROJECT_PO_DELETE: toProjectPO,
  PROJECT_PO_APPROVAL: toProjectPO,
  PROJECT_PO_HOLD: toProjectPO,
  PROJECT_PO_ASSIGN: toProjectPO,
  PROJECT_PO_CLIENT_PRICING: toProjectPO,
  PROJECT_STOCK_UPDATED: (p) => toProjectPage(p, 'stocks'),
  PROJECT_STOCK_USED_AND_WASTED: (p) => toProjectPage(p, 'stocks'),
  PROJECT_STOCK_MINIMUM_REORDER_QUANTITY: (p) => toProjectPage(p, 'stocks'),
  PROJECT_LOGISTICS_CREATE: toProjectLogistics,
  PROJECT_LOGISTICS_EDIT: toProjectLogistics,
  PROJECT_LOGISTICS_DELETE: (p) => toProjectPage(p, 'logistics'),
  PROJECT_DRIVER_UPDATE: toProjectLogistics,
  PROJECT_LABOUR_PAYMENT_ASSIGN_NEW_LABOUR: (p) => toProjectPage(p, 'labour'),
  PROJECT_LABOUR_PAYMENT_TRACKER_UPDATE: (p) => toProjectPage(p, 'labour'),
  PROJECT_LABOUR_PAYMENT_PART_PAYMENT: (p) => toProjectPage(p, 'labour'),
  PROJECT_LABOUR_PAYMENT_EDIT_PART_PAYMENT: (p) => toProjectPage(p, 'labour'),
  PROJECT_LABOUR_ATTENDANCE_ADD: (p) => toProjectPage(p, 'labour'),
  PROJECT_LABOUR_ATTENDANCE_EDIT: (p) => toProjectPage(p, 'labour'),
  PROJECT_BOM_ADD_MATERIAL: (p) => toProjectPage(p, 'bom'),
  PROJECT_BOM_EDIT_MATERIAL: (p) => toProjectPage(p, 'bom'),

  // ─── GLOBAL WAREHOUSE ─────────────────────────────────────────────────────
  GLOBAL_WAREHOUSE_CREATE: toWarehouse,
  GLOBAL_WAREHOUSE_EDIT: toWarehouse,
  GLOBAL_WAREHOUSE_DELETE: toWarehouse,
  GLOBAL_WAREHOUSE_STOCK_UPDATED: toStockLog,
  GLOBAL_WAREHOUSE_STOCK_USED_AND_WASTED: toStockLog,
  GLOBAL_WAREHOUSE_STOCK_MINIMUM_REORDER_QUANTITY: toWarehouse,

  // ─── GLOBAL MATERIAL ──────────────────────────────────────────────────────
  GLOBAL_MATERIAL_ADD_CATEGORY: '/settings/material-master',
  GLOBAL_MATERIAL_EDIT_CATEGORY: '/settings/material-master',
  GLOBAL_MATERIAL_DELETE_CATEGORY: '/settings/material-master',
  GLOBAL_MATERIAL_ADD_SUBCATEGORY: '/settings/material-master',
  GLOBAL_MATERIAL_EDIT_SUBCATEGORY: '/settings/material-master',
  GLOBAL_MATERIAL_DELETE_SUBCATEGORY: '/settings/material-master',
  GLOBAL_MATERIAL_ADD_ITEM: '/settings/material-master',
  GLOBAL_MATERIAL_EDIT_ITEM: '/settings/material-master',
  GLOBAL_MATERIAL_DELETE_ITEM: '/settings/material-master',

  // ─── GLOBAL TASKS ─────────────────────────────────────────────────────────
  GLOBAL_TASK_CREATE: (p) => toGlobalTask(p, 'details'),
  GLOBAL_TASK_UPDATE: (p) => toGlobalTask(p, 'details'),
  GLOBAL_TASK_ASSIGNEE: (p) => toGlobalTask(p, 'details'),
  GLOBAL_TASK_RAISE_ISSUE: (p) => toGlobalTask(p, 'issues'),
  GLOBAL_TASK_ISSUE_ASSIGNEE: (p) => toGlobalTask(p, 'issues'),
  GLOBAL_TASK_FILE_UPLOAD: (p) => toGlobalTask(p, 'files'),
  GLOBAL_TASK_CHECKLIST: (p) => toGlobalTask(p, 'checklist'),
  GLOBAL_TASK_LABOUR_ATTENDANCE: (p) => toGlobalTask(p, 'attendance'),
  GLOBAL_TASK_STOCK_UPDATE: (p) => toGlobalTask(p, 'stock'),
  GLOBAL_SUBTASK_CREATE: (p) => toGlobalSubTask(p, 'details'),
  GLOBAL_SUBTASK_EDIT: (p) => toGlobalSubTask(p, 'details'),
  GLOBAL_SUBTASK_ASSIGNEE: (p) => toGlobalSubTask(p, 'details'),
  GLOBAL_SUBTASK_RAISE_ISSUE: (p) => toGlobalSubTask(p, 'issues'),
  GLOBAL_SUBTASK_ISSUE_ASSIGNEE: (p) => toGlobalSubTask(p, 'issues'),
  GLOBAL_SUBTASK_FILE_UPLOAD: (p) => toGlobalSubTask(p, 'files'),
  GLOBAL_SUBTASK_CHECKLIST: (p) => toGlobalSubTask(p, 'checklist'),
  GLOBAL_SUBTASK_LABOUR_ATTENDANCE: (p) => toGlobalSubTask(p, 'attendance'),
  GLOBAL_SUBTASK_STOCK_UPDATE: (p) => toGlobalSubTask(p, 'stock'),

  // ─── GLOBAL MI / PO ───────────────────────────────────────────────────────
  GLOBAL_MI_CREATE: toGlobalMI,
  GLOBAL_MI_EDIT: toGlobalMI,
  GLOBAL_MI_DELETE: toGlobalMI,
  GLOBAL_MI_APPROVAL: toGlobalMI,
  GLOBAL_MI_HOLD: toGlobalMI,
  GLOBAL_MI_ASSIGN: toGlobalMI,
  GLOBAL_MI_DISPATCH_LOGISTICS: toGlobalMI,
  GLOBAL_MI_DISPATCH_PO: toGlobalMI,
  GLOBAL_PO_CREATE: toGlobalPO,
  GLOBAL_PO_EDIT: toGlobalPO,
  GLOBAL_PO_DELETE: toGlobalPO,
  GLOBAL_PO_APPROVAL: toGlobalPO,
  GLOBAL_PO_HOLD: toGlobalPO,
  GLOBAL_PO_ASSIGNEE: toGlobalPO,

  // ─── GLOBAL EXPENSE ───────────────────────────────────────────────────────
  GLOBAL_EXPENSE_ADD_ADVANCE: toGlobalExpense,
  GLOBAL_EXPENSE_ADD_EXPENSE: toGlobalExpense,
  GLOBAL_EXPENSE_EDIT_EXPENSE: toGlobalExpense,
  GLOBAL_EXPENSE_ADD_PAYMENT: toGlobalExpense,
  GLOBAL_EXPENSE_APPROVE: toGlobalExpense,
  GLOBAL_EXPENSE_REJECT: toGlobalExpense,
  GLOBAL_EXPENSE_DELETE: '/purchases/expenses',

  // ─── GLOBAL PO BILLS ──────────────────────────────────────────────────────
  GLOBAL_PO_BILLS_APPROVE: toGlobalPOBill,
  GLOBAL_PO_BILLS_ADD_PAYMENT: toGlobalPOBill,
  GLOBAL_PO_BILLS_PAYMENT_STATUS: toGlobalPOBill,
  GLOBAL_PO_BILLS_ASSIGNEE: toGlobalPOBill,

  // ─── GLOBAL ATTENDANCE ────────────────────────────────────────────────────
  GLOBAL_ATTENDANCE_ADD_LOCATION: toAttendanceLocations,
  GLOBAL_ATTENDANCE_EDIT_LOCATION: toAttendanceLocations,
  GLOBAL_ATTENDANCE_DELETE_LOCATION: '/attendance/locations',
  GLOBAL_ATTENDANCE_ADD_TIMECARD: '/attendance/register',
  GLOBAL_ATTENDANCE_EDIT_TIMECARD: '/attendance/register',
  GLOBAL_ATTENDANCE_ADD_SHIFT: '/attendance/shifts',
  GLOBAL_ATTENDANCE_EDIT_SHIFT: '/attendance/shifts',
  GLOBAL_ATTENDANCE_DELETE_SHIFT: '/attendance/shifts',
  GLOBAL_ATTENDANCE_MODIFY: '/attendance/register',

  // ─── GLOBAL STOCK ─────────────────────────────────────────────────────────
  GLOBAL_STOCK_UPDATED: toStockLog,
  GLOBAL_STOCK_USED_AND_WASTED: toStockLog,
  GLOBAL_STOCK_MINIMUM_REORDER_QUANTITY: toStockLog,

  // ─── GLOBAL LOGISTICS ─────────────────────────────────────────────────────
  GLOBAL_VEHICLE_ADD: toVehicleDetails,
  GLOBAL_VEHICLE_EDIT: toVehicleDetails,
  GLOBAL_VEHICLE_DELETE: toVehicleDetails,
  GLOBAL_LOGISTICS_CREATE: toGlobalLogisticsDetails,
  GLOBAL_LOGISTICS_EDIT: toGlobalLogisticsDetails,
  GLOBAL_LOGISTICS_DELETE: toGlobalLogisticsDetails,
  GLOBAL_DRIVER_UPDATE: toGlobalLogisticsDetails,

  // ─── GLOBAL ADMIN ─────────────────────────────────────────────────────────
  GLOBAL_ROLE_ADD: toRoleDetails,
  GLOBAL_ROLE_EDIT: toRoleDetails,
  GLOBAL_EMPLOYEE_ADD: toEmployeeDetails,
  GLOBAL_EMPLOYEE_EDIT: toEmployeeDetails,
  GLOBAL_EMPLOYEE_DELETE: toEmployeeDetails,
  GLOBAL_LABOUR_ADD: '/settings/labour',
  GLOBAL_LABOUR_EDIT: '/settings/labour',
  GLOBAL_VENDOR_ADD: '/vendors',
  GLOBAL_VENDOR_EDIT: '/vendors',
  GLOBAL_VENDOR_DELETE: '/vendors',
  GLOBAL_PROJECT_TYPE_ADD: '/settings/project-types',
  GLOBAL_PROJECT_TYPE_EDIT: '/settings/project-types',
  GLOBAL_PROJECT_TYPE_DELETE: '/settings/project-types',
  GLOBAL_APPROVAL_SAVE: '/company-details',
  GLOBAL_MASTER_TASK_ADD: '/settings/master-tasks',
  GLOBAL_MASTER_TASK_EDIT: '/settings/master-tasks',
  GLOBAL_MASTER_TASK_DELETE: '/settings/master-tasks',
  GLOBAL_SUBTASK_ADD: toGlobalSubTask,
  GLOBAL_SUBTASK_EDIT: toGlobalSubTask,
  GLOBAL_SUBTASK_DELETE: toGlobalSubTask,

  // ─── GLOBAL CRM ───────────────────────────────────────────────────────────
  GLOBAL_PIPELINE_CREATE: toCRMSettings,
  GLOBAL_PIPELINE_EDIT: toCRMSettings,
  GLOBAL_PIPELINE_ASSIGNEE: toCRMSettings,
  GLOBAL_STAGE_ADD: toCRMSettings,
  GLOBAL_STAGE_EDIT: toCRMSettings,
  GLOBAL_SOURCE_ADD: toCRMSettings,
  GLOBAL_SOURCE_EDIT: toCRMSettings,
  GLOBAL_CRM_LEAD_CREATE: toLeadDetails,
  GLOBAL_CRM_LEAD_EDIT: toLeadDetails,
  GLOBAL_CRM_LEAD_ASSIGNEE: toLeadDetails,
  GLOBAL_CRM_ACTIVITY_ADD: toCRMActivities,
  GLOBAL_CRM_ACTIVITY_EDIT: toCRMActivities,
  GLOBAL_CRM_ACTIVITY_ASSIGNEE: toCRMActivities,
  GLOBAL_CRM_FOLLOWUP_ADD: toCRMFollowups,
  GLOBAL_CRM_FOLLOWUP_EDIT: toCRMFollowups,
  GLOBAL_CRM_FOLLOWUP_ASSIGNEE: toCRMFollowups,
  GLOBAL_CRM_FILE_ADD: toLeadDetails,
  GLOBAL_CRM_CONVERT_PROJECT: toProjectDetails,

  // ─── GLOBAL COMPANY ───────────────────────────────────────────────────────
  GLOBAL_COMPANY_ADD: '/company-details',
};

// ─── Action → Privilege Map ─────────────────────────────────────────────────
const ACTION_PRIVILEGE_MAP = {
  // ─── PROJECT RELATED ──────────────────────────────────────────────────────
  PROJECT_PAYMENT_UPDATE_INSTALLMENT: 'project_payment_tracker_view',
  PROJECT_PAYMENT_ADD_PART_PAYMENT: 'project_payment_tracker_view',
  PROJECT_PAYMENT_EDIT_PART_PAYMENT: 'project_payment_tracker_view',
  PROJECT_TASK_CREATE: 'project_task_details_view',
  PROJECT_TASK_ASSIGNEE: 'project_task_details_view',
  PROJECT_TASK_RAISE_ISSUE: 'project_task_details_view',
  PROJECT_TASK_ISSUE_ASSIGNEE: 'project_task_details_view',
  PROJECT_TASK_FILE_UPLOAD: 'project_task_details_view',
  PROJECT_TASK_CHECKLIST: 'project_task_details_view',
  PROJECT_TASK_LABOUR_ATTENDANCE: 'project_labour_payment_labour_attendence',
  PROJECT_TASK_STOCK_UPDATE: 'project_stocks_view',
  PROJECT_SUBTASK_CREATE: 'project_task_details_view',
  PROJECT_SUBTASK_ASSIGNEE: 'project_task_details_view',
  PROJECT_SUBTASK_RAISE_ISSUE: 'project_task_details_view',
  PROJECT_SUBTASK_ISSUE_ASSIGNEE: 'project_task_details_view',
  PROJECT_SUBTASK_FILE_UPLOAD: 'project_task_details_view',
  PROJECT_SUBTASK_CHECKLIST: 'project_task_details_view',
  PROJECT_SUBTASK_LABOUR_ATTENDANCE: 'project_labour_payment_labour_attendence',
  PROJECT_SUBTASK_STOCK_UPDATE: 'project_stocks_view',
  PROJECT_FILE_UPLOAD: 'project_files_view',
  PROJECT_FILE_DELETE: 'project_files_view',
  PROJECT_MI_CREATE: 'project_mi_details_view',
  PROJECT_MI_EDIT: 'project_mi_details_view',
  PROJECT_MI_DELETE: 'project_mi_details_view',
  PROJECT_MI_APPROVAL: 'project_mi_details_view',
  PROJECT_MI_HOLD: 'project_mi_details_view',
  PROJECT_MI_ASSIGN: 'project_mi_details_view',
  PROJECT_MI_DISPATCH_LOGISTICS: 'project_mi_details_view',
  PROJECT_MI_DISPATCH_PO: 'project_mi_details_view',
  PROJECT_PO_CREATE: 'project_po_details_view',
  PROJECT_PO_EDIT: 'project_po_details_view',
  PROJECT_PO_DELETE: 'project_po_details_view',
  PROJECT_PO_APPROVAL: 'project_po_details_view',
  PROJECT_PO_HOLD: 'project_po_details_view',
  PROJECT_PO_ASSIGN: 'project_po_details_view',
  PROJECT_PO_CLIENT_PRICING: 'project_po_details_view',
  PROJECT_STOCK_UPDATED: 'project_stocks_view',
  PROJECT_STOCK_USED_AND_WASTED: 'project_stocks_view',
  PROJECT_STOCK_MINIMUM_REORDER_QUANTITY: 'project_stocks_view',
  PROJECT_LOGISTICS_CREATE: 'project_logistics_details_view',
  PROJECT_LOGISTICS_EDIT: 'project_logistics_details_view',
  PROJECT_LOGISTICS_DELETE: 'project_logistics_view',
  PROJECT_DRIVER_UPDATE: 'project_logistics_details_view',
  PROJECT_LABOUR_PAYMENT_ASSIGN_NEW_LABOUR: 'project_labour_payments_view',
  PROJECT_LABOUR_PAYMENT_TRACKER_UPDATE: 'project_labour_payments_view',
  PROJECT_LABOUR_PAYMENT_PART_PAYMENT: 'project_labour_payments_view',
  PROJECT_LABOUR_PAYMENT_EDIT_PART_PAYMENT: 'project_labour_payments_view',
  PROJECT_LABOUR_ATTENDANCE_ADD: 'project_labour_payments_view',
  PROJECT_LABOUR_ATTENDANCE_EDIT: 'project_labour_payments_view',
  PROJECT_BOM_ADD_MATERIAL: 'project_boq_view',
  PROJECT_BOM_EDIT_MATERIAL: 'project_boq_view',

  // ─── GLOBAL WAREHOUSE ─────────────────────────────────────────────────────
  GLOBAL_WAREHOUSE_CREATE: 'global_warehouse_view',
  GLOBAL_WAREHOUSE_EDIT: 'global_warehouse_view',
  GLOBAL_WAREHOUSE_DELETE: 'global_warehouse_view',
  GLOBAL_WAREHOUSE_STOCK_UPDATED: 'global_stocks_logs_view',
  GLOBAL_WAREHOUSE_STOCK_USED_AND_WASTED: 'global_stocks_logs_view',
  GLOBAL_WAREHOUSE_STOCK_MINIMUM_REORDER_QUANTITY: 'global_warehouse_view',

  // ─── GLOBAL MATERIAL ──────────────────────────────────────────────────────
  GLOBAL_MATERIAL_ADD_CATEGORY: 'global_materials_view',
  GLOBAL_MATERIAL_EDIT_CATEGORY: 'global_materials_view',
  GLOBAL_MATERIAL_DELETE_CATEGORY: 'global_materials_view',
  GLOBAL_MATERIAL_ADD_SUBCATEGORY: 'global_materials_view',
  GLOBAL_MATERIAL_EDIT_SUBCATEGORY: 'global_materials_view',
  GLOBAL_MATERIAL_DELETE_SUBCATEGORY: 'global_materials_view',
  GLOBAL_MATERIAL_ADD_ITEM: 'global_materials_view',
  GLOBAL_MATERIAL_EDIT_ITEM: 'global_materials_view',
  GLOBAL_MATERIAL_DELETE_ITEM: 'global_materials_view',

  // ─── GLOBAL TASKS ─────────────────────────────────────────────────────────
  GLOBAL_TASK_CREATE: 'global_task_view',
  GLOBAL_TASK_ASSIGNEE: 'global_task_view',
  GLOBAL_TASK_RAISE_ISSUE: 'global_task_view',
  GLOBAL_TASK_ISSUE_ASSIGNEE: 'global_task_view',
  GLOBAL_TASK_FILE_UPLOAD: 'global_task_view',
  GLOBAL_TASK_CHECKLIST: 'global_task_view',
  GLOBAL_TASK_LABOUR_ATTENDANCE: 'global_task_view',
  GLOBAL_TASK_STOCK_UPDATE: 'global_task_view',
  GLOBAL_SUBTASK_CREATE: 'global_task_view',
  GLOBAL_SUBTASK_ASSIGNEE: 'global_task_view',
  GLOBAL_SUBTASK_RAISE_ISSUE: 'global_task_view',
  GLOBAL_SUBTASK_ISSUE_ASSIGNEE: 'global_task_view',
  GLOBAL_SUBTASK_FILE_UPLOAD: 'global_task_view',
  GLOBAL_SUBTASK_CHECKLIST: 'global_task_view',
  GLOBAL_SUBTASK_LABOUR_ATTENDANCE: 'global_task_view',
  GLOBAL_SUBTASK_STOCK_UPDATE: 'global_task_view',

  // ─── GLOBAL MI / PO ───────────────────────────────────────────────────────
  GLOBAL_MI_CREATE: 'global_mi_view',
  GLOBAL_MI_EDIT: 'global_mi_view',
  GLOBAL_MI_DELETE: 'global_mi_view',
  GLOBAL_MI_APPROVAL: 'global_mi_view',
  GLOBAL_MI_HOLD: 'global_mi_view',
  GLOBAL_MI_ASSIGN: 'global_mi_view',
  GLOBAL_MI_DISPATCH_LOGISTICS: 'global_mi_view',
  GLOBAL_MI_DISPATCH_PO: 'global_mi_view',
  GLOBAL_PO_CREATE: 'global_po_view',
  GLOBAL_PO_EDIT: 'global_po_view',
  GLOBAL_PO_DELETE: 'global_po_view',
  GLOBAL_PO_APPROVAL: 'global_po_view',
  GLOBAL_PO_HOLD: 'global_po_view',
  GLOBAL_PO_ASSIGNEE: 'global_po_view',

  // ─── GLOBAL EXPENSE ───────────────────────────────────────────────────────
  GLOBAL_EXPENSE_ADD_ADVANCE: 'expenses_view',
  GLOBAL_EXPENSE_ADD_EXPENSE: 'expenses_view',
  GLOBAL_EXPENSE_EDIT_EXPENSE: 'expenses_view',
  GLOBAL_EXPENSE_ADD_PAYMENT: 'expenses_view',
  GLOBAL_EXPENSE_APPROVE: 'expenses_view',
  GLOBAL_EXPENSE_REJECT: 'expenses_view',
  GLOBAL_EXPENSE_DELETE: 'expenses_view',

  // ─── GLOBAL PO BILLS ──────────────────────────────────────────────────────
  GLOBAL_PO_BILLS_APPROVE: 'global_po_bills_view',
  GLOBAL_PO_BILLS_ADD_PAYMENT: 'global_po_bills_view',
  GLOBAL_PO_BILLS_PAYMENT_STATUS: 'global_po_bills_view',
  GLOBAL_PO_BILLS_ASSIGNEE: 'global_po_bills_view',

  // ─── GLOBAL ATTENDANCE ────────────────────────────────────────────────────
  GLOBAL_ATTENDANCE_ADD_LOCATION: 'attendance_location_view',
  GLOBAL_ATTENDANCE_EDIT_LOCATION: 'attendance_location_view',
  GLOBAL_ATTENDANCE_DELETE_LOCATION: 'attendance_location_view',
  GLOBAL_ATTENDANCE_ADD_TIMECARD: 'attendance_timecard_view',
  GLOBAL_ATTENDANCE_EDIT_TIMECARD: 'attendance_timecard_view',
  GLOBAL_ATTENDANCE_ADD_SHIFT: 'attendance_shifts_view',
  GLOBAL_ATTENDANCE_EDIT_SHIFT: 'attendance_shifts_view',
  GLOBAL_ATTENDANCE_DELETE_SHIFT: 'attendance_shifts_view',
  GLOBAL_ATTENDANCE_MODIFY: 'attendance_modify',

  // ─── GLOBAL STOCK ─────────────────────────────────────────────────────────
  GLOBAL_STOCK_UPDATED: 'global_stocks_view',
  GLOBAL_STOCK_USED_AND_WASTED: 'global_stocks_view',
  GLOBAL_STOCK_MINIMUM_REORDER_QUANTITY: 'global_stocks_view',

  // ─── GLOBAL LOGISTICS ─────────────────────────────────────────────────────
  GLOBAL_VEHICLE_ADD: 'global_vehicle_view',
  GLOBAL_VEHICLE_EDIT: 'global_vehicle_view',
  GLOBAL_VEHICLE_DELETE: 'global_vehicle_view',
  GLOBAL_LOGISTICS_CREATE: 'global_logistics_view',
  GLOBAL_LOGISTICS_EDIT: 'global_logistics_view',
  GLOBAL_LOGISTICS_DELETE: 'global_logistics_view',
  GLOBAL_DRIVER_UPDATE: 'global_logistics_view',

  // ─── GLOBAL ADMIN ─────────────────────────────────────────────────────────
  GLOBAL_ROLE_ADD: 'global_roles_view',
  GLOBAL_ROLE_EDIT: 'global_roles_view',
  GLOBAL_EMPLOYEE_ADD: 'global_employees_view',
  GLOBAL_EMPLOYEE_EDIT: 'global_employees_view',
  GLOBAL_EMPLOYEE_DELETE: 'global_employees_view',
  GLOBAL_LABOUR_ADD: 'global_labours_view',
  GLOBAL_LABOUR_EDIT: 'global_labours_view',
  GLOBAL_VENDOR_ADD: 'global_vendors_view',
  GLOBAL_VENDOR_EDIT: 'global_vendors_view',
  GLOBAL_VENDOR_DELETE: 'global_vendors_view',
  GLOBAL_PROJECT_TYPE_ADD: 'global_roles_view',
  GLOBAL_PROJECT_TYPE_EDIT: 'global_roles_view',
  GLOBAL_PROJECT_TYPE_DELETE: 'global_roles_view',
  GLOBAL_APPROVAL_SAVE: 'global_company_details_view',
  GLOBAL_MASTER_TASK_ADD: 'global_master_tasks_view',
  GLOBAL_MASTER_TASK_EDIT: 'global_master_tasks_view',
  GLOBAL_MASTER_TASK_DELETE: 'global_master_tasks_view',
  GLOBAL_SUBTASK_ADD: 'global_master_tasks_view',
  GLOBAL_SUBTASK_EDIT: 'global_master_tasks_view',
  GLOBAL_SUBTASK_DELETE: 'global_master_tasks_view',

  // ─── GLOBAL CRM ───────────────────────────────────────────────────────────
  GLOBAL_PIPELINE_CREATE: 'crm_settings_page_view',
  GLOBAL_PIPELINE_EDIT: 'crm_settings_page_view',
  GLOBAL_PIPELINE_ASSIGNEE: 'crm_settings_page_view',
  GLOBAL_STAGE_ADD: 'crm_settings_page_view',
  GLOBAL_STAGE_EDIT: 'crm_settings_page_view',
  GLOBAL_SOURCE_ADD: 'crm_settings_page_view',
  GLOBAL_SOURCE_EDIT: 'crm_settings_page_view',
  GLOBAL_CRM_LEAD_CREATE: 'crm_leads_page_view',
  GLOBAL_CRM_LEAD_EDIT: 'crm_leads_page_view',
  GLOBAL_CRM_LEAD_ASSIGNEE: 'crm_leads_page_view',
  GLOBAL_CRM_ACTIVITY_ADD: 'crm_activity_page_view',
  GLOBAL_CRM_ACTIVITY_EDIT: 'crm_activity_page_view',
  GLOBAL_CRM_ACTIVITY_ASSIGNEE: 'crm_activity_page_view',
  GLOBAL_CRM_FOLLOWUP_ADD: 'crm_followup_page_view',
  GLOBAL_CRM_FOLLOWUP_EDIT: 'crm_followup_page_view',
  GLOBAL_CRM_FOLLOWUP_ASSIGNEE: 'crm_followup_page_view',
  GLOBAL_CRM_FILE_ADD: 'crm_leads_page_view',
  GLOBAL_CRM_CONVERT_PROJECT: 'crm_leads_page_view',

  // ─── GLOBAL COMPANY ───────────────────────────────────────────────────────
  GLOBAL_COMPANY_ADD: 'global_company_details_view',
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Resolve a notification's `action_type` + `params` into a frontend route.
 *
 * @param {string|null|undefined} actionType  e.g. "GLOBAL_PIPELINE_EDIT"
 * @param {Record<string, unknown>} params    Parsed params object
 * @returns {string|null}                     Route path, or null if unknown
 */
export const resolveNotificationRoute = (actionType, params = {}) => {
  if (!actionType) return null;

  // Enforce privilege checks and fall back to home page '/' if the user is not authorized
  const requiredPrivilege = ACTION_PRIVILEGE_MAP[actionType];
  if (requiredPrivilege) {
    const hasPriv = useRBACStore.getState().hasPrivilege(requiredPrivilege);
    if (!hasPriv) {
      console.warn(`[Notification Route Resolver] User lacks privilege '${requiredPrivilege}' for action '${actionType}'. Falling back to '/'.`);
      return '/';
    }
  }

  const resolver = ACTION_ROUTE_MAP[actionType];
  if (!resolver) return null;

  // Support both static strings and builder functions
  return typeof resolver === 'function' ? resolver(params) : resolver;
};
